/**
 * Alerts — the one list every alert count in Meridian opens (OP#1854).
 *
 * Route: /o/:orgId/alerts and /o/:orgId/s/:siteId/alerts, filters in the URL
 * (?severity=critical|warn, ?kind=<alert type>) so a count can deep-link to exactly the rows it
 * counted and the view survives reload / sharing.
 *
 * Each row answers the four questions an operator has about an alert:
 *   WHAT        plain-language label + the controller's own message (severity via StatusPill)
 *   WHICH       device name, serial, model and site, linking to the device sheet
 *   SINCE WHEN  first detected (absolute time + duration); for offline, the last contact
 *   WHAT TO DO  a suggested action per alert type (lib/alertGuide)
 *
 * Design rules (system design): neutral surfaces (bg-surface / surface-2, border-line, ink /
 * ink-muted), the status ramp ONLY for alert state, brand violet ONLY for interactive controls
 * (filter chips, buttons). Tablet-first: rows are stacked cards at every width, with the
 * action block flowing under the facts on narrow screens and beside them from `lg`.
 *
 * Failure is explicit: a failed fetch renders an error panel with Retry, never "No open alerts".
 */
import { useSearchParams, Link } from "react-router-dom";
import { useHealthWorklist } from "@/lib/api/hooks";
import { useSession } from "@/stores/session";
import { orgLabel } from "@/lib/quarantine";
import { PageHeader, EmptyState } from "@/components/ui/Page";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/api/client";
import { fmtAbsolute, parseApiTime } from "@/lib/format";
import { cn } from "@/lib/cn";
import { alertGuide, durationLabel, parseSeverity } from "@/lib/alertGuide";
import { lldpAge } from "@/lib/lldpAge";
import { formatPortRefs } from "@/lib/switchPorts";
import type { HealthAlert } from "@/types/domain";

/** How many rows one page shows. Open alerts are a short list in practice; the header says
 *  when more exist than are shown, so nothing is silently truncated. */
const PAGE_LIMIT = 200;

export default function Alerts() {
  const [params, setParams] = useSearchParams();
  const severity = parseSeverity(params.get("severity"));
  const kind = params.get("kind") || null;
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
// Quarantine holding area is labelled, never shown by its stored name (2026-10-19).
  const orgName = useSession((s) => orgLabel(s.currentOrg()));
  const siteName = useSession((s) => s.currentSite()?.name);

  const q = useHealthWorklist({ severity, kind, limit: PAGE_LIMIT });
  const items = q.data?.items ?? [];
  const summary = q.data?.summary;
  const scopeLabel = siteName ?? orgName ?? "this scope";

  /** Replace one URL filter, keeping the others. */
  const setFilter = (key: "severity" | "kind", value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const deviceHref = (serial: string) =>
    siteId ? `/o/${orgId}/s/${siteId}/devices/${serial}` : `/o/${orgId}/devices/${serial}`;

  return (
    <div>
      <PageHeader
        title="Alerts"
        subtitle={`Open alerts for ${scopeLabel}, most urgent first`}
      />

      {/* Filters. Counts come from the server summary for the scope, so a chip's number equals
          the rows it shows (the same contract as the tiles that link here). */}
      <div className="mb-4 flex flex-wrap items-center gap-2" role="group" aria-label="Filter alerts">
        <FilterChip active={!severity} onClick={() => setFilter("severity", null)}>
          All{summary ? ` ${summary.total}` : ""}
        </FilterChip>
        <FilterChip active={severity === "critical"} onClick={() => setFilter("severity", "critical")}>
          Critical{summary ? ` ${summary.critical}` : ""}
        </FilterChip>
        <FilterChip active={severity === "warn"} onClick={() => setFilter("severity", "warn")}>
          Warning{summary ? ` ${summary.warn}` : ""}
        </FilterChip>
        {kind && (
          <FilterChip active onClick={() => setFilter("kind", null)} ariaLabel={`Remove filter ${alertGuide(kind).label}`}>
            {alertGuide(kind).label} <span aria-hidden>×</span>
          </FilterChip>
        )}
      </div>

      {q.isError ? (
        <LoadError error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} />
      ) : q.isLoading ? (
        <div className="md-card p-4 text-sm text-ink-muted" aria-busy>
          Loading alerts…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title={severity || kind ? "No open alerts match this filter" : "No open alerts"}
          hint={
            severity || kind
              ? "Clear the filter to see every open alert in this scope."
              : `Nothing needs attention at ${scopeLabel} right now.`
          }
          action={
            severity || kind ? (
              <Button variant="secondary" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {q.data && q.data.total > items.length && (
            <p role="status" className="mb-2 text-xs text-ink-muted">
              Showing the {items.length} most urgent of {q.data.total} open alerts.
            </p>
          )}
          <ul className="md-stagger flex flex-col gap-2" aria-label="Open alerts">
            {items.map((a) => (
              <AlertRow key={a.id} alert={a} deviceHref={deviceHref} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** One alert: what / which device / since when / what to do. */
function AlertRow({ alert, deviceHref }: { alert: HealthAlert; deviceHref: (serial: string) => string }) {
  const guide = alertGuide(alert.kind, alert.device_kind);
  const now = Date.now();
  const since = parseApiTime(alert.created_at);
  const isOffline = alert.kind === "offline";
  const lastSeen = parseApiTime(alert.device_last_seen);
  const deviceLabel = alert.device_name || alert.target_id;
  // Where it was plugged in, and how old that is (OP#1854 AC): offline -> "last seen 11d ago".
  const uplink = alert.device_lldp?.system_name ? alert.device_lldp : null;
  const uplinkAge = uplink ? lldpAge(uplink, !isOffline, now) : null;

  return (
    <li
      className="md-card grid gap-3 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]"
      data-testid="alert-row"
      data-kind={alert.kind}
    >
      <div className="min-w-0">
        {/* WHAT */}
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={alert.severity} />
          <h2 className="text-base font-medium text-ink">{guide.label}</h2>
        </div>
        {alert.title && <p className="mt-1 text-sm text-ink-muted">{alert.title}</p>}

        {/* WHICH DEVICE */}
        <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <div className="min-w-0">
            <dt className="text-xs text-ink-subtle">Device</dt>
            <dd className="min-w-0 truncate">
              {alert.target_type === "device" ? (
                <Link to={deviceHref(alert.target_id)} className="font-medium text-ink underline-offset-2 hover:underline">
                  {deviceLabel}
                </Link>
              ) : (
                <span className="text-ink">{deviceLabel}</span>
              )}
              <span className="ml-2 font-mono text-xs text-ink-muted">
                {alert.device_name ? alert.target_id : ""}
                {alert.device_model ? `${alert.device_name ? " · " : ""}${alert.device_model}` : ""}
              </span>
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-ink-subtle">Site</dt>
            <dd className="truncate text-ink">{alert.site_name ?? (alert.site_id ? `Site ${alert.site_id}` : "Not assigned")}</dd>
          </div>

          {/* SINCE WHEN */}
          <div>
            <dt className="text-xs text-ink-subtle">Since</dt>
            <dd className="text-ink">
              <time dateTime={alert.created_at}>{fmtAbsolute(alert.created_at)}</time>
              <span className="text-ink-muted"> · {durationLabel(since, now)}</span>
            </dd>
          </div>
          {isOffline && (
            <div>
              <dt className="text-xs text-ink-subtle">Last contact</dt>
              <dd className="text-ink">
                {lastSeen === null ? (
                  "Never"
                ) : (
                  <>
                    <time dateTime={alert.device_last_seen ?? undefined}>{fmtAbsolute(alert.device_last_seen)}</time>
                    <span className="text-ink-muted"> · {durationLabel(lastSeen, now)} ago</span>
                  </>
                )}
              </dd>
            </div>
          )}
          {isOffline && (
            <div className="min-w-0 sm:col-span-2">
              <dt className="text-xs text-ink-subtle">Last uplink (LLDP)</dt>
              <dd className="min-w-0 truncate text-ink" data-testid="alert-last-uplink">
                {uplink && uplinkAge ? (
                  <>
                    <span className="font-mono text-xs">
                      {uplink.system_name}
                      {uplink.port_id ? ` · port ${formatPortRefs(uplink.port_id)}` : ""}
                    </span>
                    <span className="text-ink-muted"> · {uplinkAge.label}</span>
                  </>
                ) : (
                  <span className="text-ink-muted">Not known (no LLDP neighbour reported)</span>
                )}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* WHAT TO DO. A neutral well, not a status colour: advice is not a state. */}
      <div className="rounded-lg border border-line bg-surface-2 p-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">What to do</div>
        <p className="mt-1 text-sm text-ink" data-testid="alert-action">{guide.action}</p>
        {alert.target_type === "device" && (
          <div className="mt-3">
            <Link to={deviceHref(alert.target_id)}>
              <Button variant="secondary">Open device</Button>
            </Link>
          </div>
        )}
      </div>
    </li>
  );
}

/** The failed-fetch state: says what failed and offers a retry. Never an empty list. */
function LoadError({ error, onRetry, retrying }: { error: unknown; onRetry: () => void; retrying: boolean }) {
  const transport = error instanceof ApiError && error.isTransport;
  return (
    <div role="alert" className="md-card border-critical/40 bg-critical-soft p-4 text-sm">
      <div className="font-medium text-critical">Alerts could not be loaded</div>
      <p className="mt-1 text-ink-muted">
        {transport
          ? "Meridian could not reach the controller. The alerts are still there; this page just cannot show them right now."
          : `The controller answered with an error: ${error instanceof Error ? error.message : String(error)}`}
      </p>
      <div className="mt-3">
        <Button variant="secondary" onClick={onRetry} disabled={retrying}>
          {retrying ? "Retrying…" : "Retry"}
        </Button>
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
  ariaLabel,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel}
      className={cn(
        "min-h-touch rounded-full border px-3 text-xs tabular-nums transition-colors",
        active ? "border-brand bg-brand/10 text-brand" : "border-line text-ink-muted hover:bg-surface-2",
      )}
    >
      {children}
    </button>
  );
}

